/**
 * Returns the count of dashboards created after the user last visited
 * the Dashboards gallery. Drives the red badge on the Sidebar nav item.
 *
 * Polls the dashboard list every 2 minutes when the user is NOT on the
 * /dashboards page. Resets to 0 and stops polling on that page.
 */

import { useState, useEffect, useRef, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { api } from '../services/api';

export const LAST_SEEN_KEY = 'compass:dashboards:lastSeenAt';
const POLL_MS = 2 * 60 * 1000;

/**
 * @param enabled Pass `false` when the user cannot open the Dashboards tab
 *   (no `dashboards-access` capability). The API behind the tab is gated the
 *   same way (capabilityGate('/api/dashboards') in src/app.ts), so polling it
 *   would just produce a 403 every two minutes; a tab the user cannot see
 *   needs no badge either.
 */
export function useNewDashboardCount(enabled = true): number {
  const location = useLocation();
  const isOnGallery = location.pathname === '/dashboards';
  const [count, setCount] = useState(0);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const check = useCallback(async () => {
    try {
      const raw = localStorage.getItem(LAST_SEEN_KEY);
      if (!raw) return;
      const lastSeenTs = new Date(raw).getTime();
      const res = await api.listDashboards();
      const n = res.data.filter(
        (d) => new Date(d.generatedAt).getTime() > lastSeenTs,
      ).length;
      setCount(n);
    } catch {
      // non-blocking — badge just stays stale
    }
  }, []);

  useEffect(() => {
    if (!enabled || isOnGallery) {
      setCount(0);
      return;
    }

    check();
    timerRef.current = setInterval(check, POLL_MS);

    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [enabled, isOnGallery, check]);

  return count;
}
